"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_components/ui/select";
import {
  ANOTHER_STATE_OPTION,
  MARBLE_STATES,
} from "@/app/_components/states/constants";
import { StateToStr } from "@/lib/enumMappings";
import { useTRPC } from "@/trpc/client";
import { State } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";

interface StateDropdownProps {
  value?: State;
  onValueChange?: (value: State) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onlyActiveStates?: boolean;
  // Show states open for school worker signups: active states plus
  // not-yet-active states with allowSchoolWorkerSignups enabled.
  // Takes precedence over onlyActiveStates.
  onlySchoolWorkerSignupStates?: boolean;
  // Show states open for new referrals: active states that are also
  // accepting referrals. Takes precedence over onlySchoolWorkerSignupStates
  // and onlyActiveStates.
  onlyReferralOpenStates?: boolean;
  // The counselor a referral is being submitted on behalf of (a guardian
  // referral link's schoolWorkerId). Their allowlisting also opens a
  // soft-launch state, so the guardian who received the link can pick it.
  attributedSchoolWorkerId?: number;
  // Hide these states from the list (e.g. states that already have a
  // configuration when creating a new one). A controlled `value` in this
  // list is still shown so an existing selection stays visible.
  excludeStates?: State[];
  // Set on the dropdown menu for a lavender surface, since the menu portals
  // outside the themed root. Never default this on: most callers are teal.
  menuTheme?: "lavender";
  // Append an "Another state" option after the filtered state list, and
  // never fall back to the full state list: while the filtered list loads
  // the dropdown is disabled, and on a failed/empty fetch it uses the
  // hardcoded Marble states. Caveat: picking "Another state" calls
  // onValueChange with ANOTHER_STATE_OPTION, which is not a real State —
  // the consuming form must warn and block submission on that value.
  showAnotherStateOption?: boolean;
}

export function StateDropdown({
  value,
  onValueChange,
  placeholder = "Select state",
  disabled = false,
  className,
  onlyActiveStates = false,
  onlySchoolWorkerSignupStates = false,
  onlyReferralOpenStates = false,
  attributedSchoolWorkerId,
  excludeStates,
  showAnotherStateOption = false,
  menuTheme,
}: StateDropdownProps) {
  const trpc = useTRPC();

  const filterStates =
    onlyReferralOpenStates || onlySchoolWorkerSignupStates || onlyActiveStates;

  const { data: referralOpenStates, isPending: isReferralOpenStatesPending } =
    useQuery(
      trpc.states.getReferralOpenStates.queryOptions(
        attributedSchoolWorkerId ? { attributedSchoolWorkerId } : undefined,
        { enabled: onlyReferralOpenStates },
      ),
    );

  const {
    data: schoolWorkerSignupStates,
    isPending: isSchoolWorkerSignupStatesPending,
  } = useQuery(
    trpc.states.getSchoolWorkerSignupStates.queryOptions(undefined, {
      enabled: onlySchoolWorkerSignupStates && !onlyReferralOpenStates,
    }),
  );

  const { data: fetchedActiveStates, isPending: isActiveStatesPending } =
    useQuery(
      trpc.states.getActiveStates.queryOptions(undefined, {
        enabled:
          onlyActiveStates &&
          !onlySchoolWorkerSignupStates &&
          !onlyReferralOpenStates,
      }),
    );

  let fetchedStates: State[] | undefined;
  let isFetchedStatesPending: boolean;
  if (onlyReferralOpenStates) {
    fetchedStates = referralOpenStates;
    isFetchedStatesPending = isReferralOpenStatesPending;
  } else if (onlySchoolWorkerSignupStates) {
    fetchedStates = schoolWorkerSignupStates;
    isFetchedStatesPending = isSchoolWorkerSignupStatesPending;
  } else {
    fetchedStates = fetchedActiveStates;
    isFetchedStatesPending = isActiveStatesPending;
  }

  // With the "Another state" option the dropdown is disabled while the list
  // loads instead of falling back below.
  const isLoading =
    showAnotherStateOption && filterStates && isFetchedStatesPending;

  let statesToShow: State[];
  if (filterStates && fetchedStates?.length) {
    statesToShow = fetchedStates;
  } else if (filterStates && showAnotherStateOption) {
    // Loading (dropdown disabled) or failed/empty fetch: use the hardcoded
    // Marble states rather than ever pairing "Another state" with the full
    // state list.
    statesToShow = [...MARBLE_STATES];
  } else {
    // While loading, or if the fetch failed or returned nothing, fall back
    // to showing all states.
    statesToShow = Object.keys(StateToStr) as State[];
  }

  if (excludeStates?.length) {
    statesToShow = statesToShow.filter(
      (state) => !excludeStates.includes(state),
    );
  }

  // Keep a controlled value visible across the loading → fetched item
  // remount. Otherwise Radix shows the placeholder and fires
  // onValueChange("") — which was wiping the referral-form prefill.
  if (
    value &&
    (value as string) !== ANOTHER_STATE_OPTION &&
    !statesToShow.includes(value)
  ) {
    statesToShow = [...statesToShow, value];
  }

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        // Radix fires "" when the current selection isn't in the option
        // list (item remount, or a prefill that isn't a filtered option).
        // That is not a user pick — ignore it so we don't clear the field.
        if (!next) {
          return;
        }
        onValueChange?.(next as State);
      }}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className={className}>
        <SelectValue
          placeholder={isLoading ? "Loading states..." : placeholder}
        />
      </SelectTrigger>
      <SelectContent data-theme={menuTheme}>
        {statesToShow.map((state) => (
          <SelectItem key={state} value={state}>
            {StateToStr[state] || state}
          </SelectItem>
        ))}
        {showAnotherStateOption && (
          <SelectItem value={ANOTHER_STATE_OPTION}>Another state</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
